"use client";
import { forwardRef, useCallback, useState, useEffect, useRef } from "react"
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"
import {
  Select,
  MenuItem,
  Divider,
  Box,
  TextField,
} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"

const PRESET_COLORS = [
  { label: "기본", value: null },
  { label: "검정", value: "#000000" },
  { label: "회색", value: "#666666" },
  { label: "빨강", value: "#FF0000" },
  { label: "주황", value: "#FF8800" },
  { label: "노랑", value: "#FFD700" },
  { label: "초록", value: "#00AA00" },
  { label: "파랑", value: "#0000FF" },
  { label: "보라", value: "#8800FF" },
  { label: "분홍", value: "#FF00FF" },
]

export const TextColorPopover = forwardRef((
  {
    editor: providedEditor,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [customColor, setCustomColor] = useState("#000000")
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectOpen, setSelectOpen] = useState(false)
  const debounceTimerRef = useRef(null)

  const handleColorChange = useCallback((event) => {
    if (!editor) return
    const selectedValue = event.target.value
    
    if (selectedValue) {
      editor.chain().focus().setColor(selectedValue).run()
    } else {
      editor.chain().focus().unsetColor().run()
    }
    setSelectOpen(false)
  }, [editor])

  const applyColorChange = useCallback((color) => {
    if (!editor || !color) return
    editor.chain().focus().setColor(color).run()
  }, [editor])

  // Debounce를 위한 useEffect
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (customColor && /^#[0-9A-F]{6}$/i.test(customColor)) {
      debounceTimerRef.current = setTimeout(() => {
        applyColorChange(customColor)
      }, 1000)
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [customColor, applyColorChange])

  const currentColor = editor?.getAttributes('textStyle')?.color || null

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
    setSelectOpen(true)
  }

  const handleClose = () => {
    // 메뉴가 닫힐 때 아직 적용되지 않은 색상이 있으면 즉시 적용
    if (debounceTimerRef.current && customColor && /^#[0-9A-F]{6}$/i.test(customColor)) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
      applyColorChange(customColor)
    }
    setSelectOpen(false)
    setAnchorEl(null)
  }

  return (
    <Select
      ref={ref}
      value={currentColor || ""}
      onChange={handleColorChange}
      onOpen={handleOpen}
      onClose={handleClose}
      open={selectOpen}
      displayEmpty
      size="small"
      renderValue={(selected) => {
        return (
          <span style={{ 
            color: selected || 'currentColor',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>A</span>
        )
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 400,
            '& .MuiMenuItem-root': {
              minHeight: 36,
            }
          }
        },
        onClose: handleClose,
      }}
      sx={{
        height: '32px',
        fontSize: '14px',
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
      {...buttonProps}
    >
      <MenuItem disabled sx={{ py: 0, minHeight: 'auto' }}>
        <Divider sx={{ width: '100%' }} />
      </MenuItem>
      <MenuItem 
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        sx={{ py: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
          <TextField
            size="small"
            type="color"
            value={customColor}
            onChange={(e) => {
              const newColor = e.target.value
              setCustomColor(newColor)
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ width: '60px', height: '40px' }}
            inputProps={{
              style: { height: '40px', padding: '2px' }
            }}
          />
          <TextField
            size="small"
            type="text"
            placeholder="#000000"
            value={customColor}
            onChange={(e) => {
              const newColor = e.target.value
              setCustomColor(newColor)
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ flex: 1 }}
          />
        </Box>
      </MenuItem>
      <MenuItem disabled sx={{ py: 0, minHeight: 'auto' }}>
        <Divider sx={{ width: '100%' }} />
      </MenuItem>
      {PRESET_COLORS.map((color) => (
        <MenuItem
          key={color.value || 'default'}
          value={color.value || ""}
          selected={currentColor === color.value}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {color.value && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: color.value,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              )}
              <span style={{ color: color.value || 'currentColor' }}>{color.label}</span>
            </Box>
            {currentColor === color.value && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
})

TextColorPopover.displayName = "TextColorPopover"

export default TextColorPopover
